import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Switch, Space, App } from 'antd';
import { EditOutlined, PlusOutlined, SafetyOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi, PermissionTreeView } from '@zero/auth-admin-ui';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';

export function RolesPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allPerms, setAllPerms] = useState([]);
  const [editing, setEditing] = useState(null);      // role đang sửa QUYỀN
  const [editPerms, setEditPerms] = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);   // modal tạo mới / sửa thông tin
  const [formRole, setFormRole] = useState(null);    // null = tạo mới
  const [pageSize, setPageSize] = useGridPageSize('roles', 25);

  const refresh = () => {
    setLoading(true);
    api.listRoles({ offset: 0, limit: 100 })
      .then((r) => setRoles(r.data?.Data?.Items ?? []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); api.listPermissions().then((r) => setAllPerms(r.data?.Data ?? [])); /* eslint-disable-next-line */ }, []);

  // Sửa QUYỀN của role.
  // PHẢI nạp quyền hiện tại trước: trước đây openEdit set editPerms = [] rồi lưu thẳng,
  // nên chỉ cần mở ra bấm OK là XOÁ SẠCH quyền của role (VMTSuperAdmin mất toàn bộ quyền).
  const openPerms = async (role) => {
    setEditing(role);
    setEditPerms([]);
    setPermLoading(true);
    try {
      const r = await api.getRolePermissions(role.RoleID);
      const list = r.data?.Data ?? [];
      setEditPerms(list.map((p) => (typeof p === 'object' ? p.PermissionID : p)));
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
      setEditing(null);          // không cho sửa mù — tránh lưu nhầm danh sách rỗng
    } finally {
      setPermLoading(false);
    }
  };

  const savePerms = async () => {
    setSaving(true);
    try {
      await api.setRolePermissions(editing.RoleID, editPerms);
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      setEditing(null);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  // Tạo mới / sửa thông tin role. forceRender để form đã mount trước khi setFieldsValue.
  const openForm = (role = null) => {
    setFormRole(role);
    setFormOpen(true);
    form.setFieldsValue({
      Name:        role?.Name ?? '',
      Description: role?.Description ?? '',
      Require2FA:  !!role?.Require2FA,
    });
  };

  const saveForm = async (values) => {
    setSaving(true);
    try {
      if (formRole) await api.updateRole(formRole.RoleID, values);
      else          await api.createRole(values);
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      setFormOpen(false);
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  // Xoá vai trò. BE chặn sẵn role built-in (ROLE_BUILTIN) và tự bump security stamp
  // của những user đang giữ role → token của họ mất quyền ngay, không phải đăng nhập lại.
  const removeRole = (role) => modal.confirm({
    title:   t('role.deleteConfirmTitle', { defaultValue: 'Xoá vai trò này?' }),
    content: t('role.deleteConfirmContent', {
      name: role.Name,
      defaultValue: `Vai trò "${role.Name}" sẽ bị xoá. Người dùng đang được gán vai trò này sẽ MẤT toàn bộ quyền do nó cấp.`,
    }),
    icon:   <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
    okText: t('common.delete'),
    okButtonProps: { danger: true },
    cancelText: t('common.cancel'),
    onOk: async () => {
      try {
        await api.deleteRole(role.RoleID);
        message.success(t('common.deleted'));
        refresh();
      } catch (err) {
        message.error(err?.response?.data?.Message ?? err.message);
        throw err;                        // giữ hộp thoại mở để admin đọc được lỗi
      }
    },
  });

  const columns = [
    { title: t('common.id'),         dataIndex: 'RoleID',      width: 80 },
    { title: t('common.name'),       dataIndex: 'Name',        render: (v) => <strong>{v}</strong> },
    { title: t('common.description'),dataIndex: 'Description', ellipsis: true },
    { title: '2FA',                  dataIndex: 'Require2FA',  width: 80,  render: (v) => v ? <Tag color="orange">{t('common.yes')}</Tag> : '-' },
    { title: t('role.builtIn'),      dataIndex: 'IsBuiltIn',   width: 100, render: (v) => v ? <Tag>Built-in</Tag> : '-' },
    { title: t('common.actions'),    key: 'a', width: 150, render: (_, r) => (
      <Space size={4}>
        <Button size="small" icon={<EditOutlined />} title={t('common.edit')} onClick={() => openForm(r)} />
        <Button size="small" icon={<SafetyOutlined />} title={t('role.permissions')} onClick={() => openPerms(r)} />
        <Button size="small" danger icon={<DeleteOutlined />} title={t('common.delete')}
                disabled={!!r.IsBuiltIn} onClick={() => removeRole(r)} />
      </Space>
    ) },
  ];

  return (
    <Card
      size="small"
      extra={
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          {t('common.createNew')}
        </Button>
      }
    >
      <Table dataSource={roles} columns={columns} loading={loading} rowKey="RoleID" size="small" bordered
             onRow={(r) => ({ onDoubleClick: () => openForm(r) })}
             pagination={{
               pageSize,
               showSizeChanger: true,
               pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
               onShowSizeChange: (_, size) => setPageSize(size),
             }}
             scroll={{ y: 'calc(100vh - 200px)' }} />

      <Modal maskClosable={false} keyboard={false}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        forceRender
        title={formRole ? `${t('common.edit')} — ${formRole.Name}` : t('common.createNew')}
      >
        <Form form={form} layout="vertical" onFinish={saveForm} autoComplete="off">
          <Form.Item name="Name" label={t('common.name')} rules={[{ required: true, max: 100 }]}>
            <Input disabled={!!formRole?.IsBuiltIn} placeholder="VMTSuperAdmin" />
          </Form.Item>
          <Form.Item name="Description" label={t('common.description')}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item name="Require2FA" label="2FA" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal maskClosable={false} keyboard={false} open={!!editing} onCancel={() => setEditing(null)} onOk={savePerms}
             confirmLoading={saving} okButtonProps={{ disabled: permLoading }}
             title={`${t('role.permissions')} — ${editing?.Name}`} width={1000}>
        <PermissionTreeView permissions={allPerms} selectedIds={editPerms} onChange={setEditPerms} />
      </Modal>
    </Card>
  );
}
